import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies -- ky is a runtime dependency in apps/web (monorepo workspace)
import ky from 'ky';
import * as React from 'react';
import { countries } from '../config';
import { getEdgeUrl, safeLocalStorage } from '../helpers';

// Define types for geo data matching the edge function response
interface GeoData {
  country?: string;
  code?: string;
  currency?: Currency; // Add currency to the geo data
}

interface GeoContextValue {
  geoData: GeoData | null;
  isLoading: boolean;
  error: Error | null;
  refreshGeoData: () => void;
}

// Cache configuration
const CACHE_KEY = 'ch-gd';
const CACHE_EXPIRY_HOURS = 12; // Cache for 12 hours

// Create context
const GeoContext = createContext<GeoContextValue | undefined>(undefined);

// Helper function to derive currency from country code
const deriveCurrency = (countryCode: string): Currency => {
  const defaultCurrency: Currency = 'USD';
  
  // Find country by ISO code
  const country = countries.find((x) => x.iso === countryCode);
  
  if (!country) {
    return defaultCurrency;
  }
  
  // List of supported currencies in your pricing
  const supportedCurrencies: Currency[] = ['USD', 'EUR', 'GBP', 'AUD'];
  const countryCurrency = country.currency as Currency;
  
  // Check if the country's currency is supported
  if (supportedCurrencies.includes(countryCurrency)) {
    return countryCurrency;
  }
  
  return defaultCurrency;
};

// Provider component
export const GeoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [geoData, setGeoData] = useState<GeoData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchGeoData = async (): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Check cache first
      const cachedData = safeLocalStorage.get<GeoData>(CACHE_KEY, CACHE_EXPIRY_HOURS);
      if (cachedData) {
        setGeoData(cachedData);
        setIsLoading(false);
        return;
      }

      // Fetch fresh data
      const geoUrl = getEdgeUrl('geo');
      const data: GeoData = await ky.get(geoUrl).json<GeoData>();
      
      // Derive currency from country code before storing
      if (data.code) {
        data.currency = deriveCurrency(data.code);
      } else {
        data.currency = 'USD'; // Default currency
      }
      
      setGeoData(data);
      safeLocalStorage.set(CACHE_KEY, data, CACHE_EXPIRY_HOURS); // Cache the fresh data with currency
    } catch (err) {
      // Set default geo data with default currency on error
      const defaultData: GeoData = { currency: 'USD' };
      setGeoData(defaultData);
      setError(err instanceof Error ? err : new Error('Failed to fetch geo data'));
    } finally {
      setIsLoading(false);
    }
  };

  const refreshGeoData = (): void => {
    // Clear cache and refetch
    safeLocalStorage.remove(CACHE_KEY);
    void fetchGeoData();
  };

  useEffect(() => {
    // Only run on client side
    if (typeof window !== 'undefined') {
      void fetchGeoData();
    }
  }, []);

  const value: GeoContextValue = {
    geoData,
    isLoading,
    error,
    refreshGeoData
  };

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
};

// Custom hook to use the geo context
export const useGeoContext = (): GeoContextValue => {
  const context = useContext(GeoContext);
  if (context === undefined) {
    throw new Error('useGeoContext must be used within a GeoProvider');
  }
  return context;
};

// Export types for external use
export type { GeoData, GeoContextValue };
